import React, { useState, useRef, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useMT5Data } from './MT5TradingPanel/MT5TradingPanel.component';
import { AssetDto } from '../services/types';

interface MT5LiveChartPanelProps {
  isConnected: boolean;
  onClose: () => void;
}

const SYMBOLS = ['EURUSD', 'GBPUSD', 'XAUUSD', 'USOIL'];

const MT5LiveChartPanel: React.FC<MT5LiveChartPanelProps> = ({ isConnected, onClose }) => {
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('mt5LiveChartPanelPosition');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return { x: 100, y: 100 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);
  const [selectedSymbol, setSelectedSymbol] = useState('EURUSD');
  const [priceHistory, setPriceHistory] = useState<{ time: string; price: number }[]>([]);

  // Save position to localStorage
  useEffect(() => {
    localStorage.setItem('mt5LiveChartPanelPosition', JSON.stringify(position));
  }, [position]);

  // Draggable logic (header only)
  const headerRef = useRef<HTMLDivElement>(null);
  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left mouse button
    setIsDragging(true);
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
    e.stopPropagation();
    e.preventDefault();
  };
  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      const maxX = window.innerWidth - 420;
      const maxY = window.innerHeight - 320;
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };
  const handleMouseUp = () => setIsDragging(false);
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Get live data from React Query
  const { data, isFetching } = useMT5Data(isConnected);
  const marketData: AssetDto[] = (data as any)?.marketData || [];

  // Update price history for selected symbol
  useEffect(() => {
    if (!marketData.length) return;
    const asset = marketData.find(a => a.symbol === selectedSymbol);
    if (!asset) return;
    setPriceHistory(prev => {
      const now = new Date();
      const time = now.toLocaleTimeString();
      // Only add if price changed or time is new
      if (!prev.length || prev[prev.length - 1].price !== asset.currentPrice) {
        const next = [...prev, { time, price: asset.currentPrice }];
        // Keep last 50 points
        return next.slice(-50);
      }
      return prev;
    });
  }, [marketData, selectedSymbol]);

  // Reset price history when symbol changes
  useEffect(() => {
    setPriceHistory([]);
  }, [selectedSymbol]);

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        width: 420,
        background: '#fff',
        border: '1px solid #dee2e6',
        borderRadius: 10,
        boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
        zIndex: 2000,
        userSelect: 'none',
        cursor: isDragging ? 'grabbing' : 'default',
        fontFamily: 'inherit',
        minHeight: 320
      }}
    >
      <div
        ref={headerRef}
        onMouseDown={handleHeaderMouseDown}
        style={{
          display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #eee', background: '#f8f9fa', borderTopLeftRadius: 10, borderTopRightRadius: 10,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 16, color: '#495057', marginRight: 8 }}>📈 Live Forex Chart</span>
        <select
          value={selectedSymbol}
          onChange={e => setSelectedSymbol(e.target.value)}
          style={{ marginLeft: 12, fontSize: 14, borderRadius: 4, border: '1px solid #ccc', padding: '2px 8px' }}
        >
          {SYMBOLS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          onClick={onClose}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#888', fontSize: 18, cursor: 'pointer' }}
          title="Close"
        >✖</button>
        <button
          onClick={() => {
            setPosition({ x: 100, y: 100 });
            localStorage.removeItem('mt5LiveChartPanelPosition');
          }}
          style={{ marginLeft: 8, background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer' }}
          title="Reset position"
        >↺</button>
      </div>
      <div style={{ padding: 16, height: 260 }}>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={priceHistory} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" minTickGap={20} />
            <YAxis domain={['auto', 'auto']} tickFormatter={v => typeof v === 'number' ? v.toFixed(4) : v} />
            <Tooltip formatter={(v: number | string) => typeof v === 'number' ? v.toFixed(5) : v} />
            <Line type="monotone" dataKey="price" stroke="#007bff" dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
        {isFetching && <div style={{ textAlign: 'center', color: '#888', fontSize: 13, marginTop: 8 }}>Updating...</div>}
      </div>
    </div>
  );
};

export default MT5LiveChartPanel; 